import {getKeycloak} from "@auth/keycloak"

import {localStorageItemName} from "src/constants/local-storage-item-name"

/**
 * Seconds of remaining validity below which the access token is renewed before
 * being used. Wide enough to cover a slow request reaching the API after the
 * token was read.
 */
const MIN_VALIDITY_SECONDS = 30

/**
 * Renews the access token when it is close to expiring, then returns it.
 *
 * Every outgoing call goes through this, so a request never leaves carrying a
 * token the API will refuse. It is cheap to call per request: `updateToken` is a
 * no-op while the token still has more than `MIN_VALIDITY_SECONDS` left, and
 * keycloak-js queues concurrent callers onto a single refresh request.
 *
 * Renewing also has a second, less obvious job. Each refresh hits Keycloak's
 * token endpoint, which is what re-arms the realm's `SSO Session Idle` timer.
 * Under the previous implicit flow nothing ever called that endpoint, so the SSO
 * session expired while the user was still working — and the next redirect came
 * back as a full login screen.
 *
 * Never throws: a failed renewal returns whatever token is on hand and lets the
 * API answer 401, which the network layer turns into a forced renewal. That
 * keeps a transient Keycloak hiccup from breaking every screen at once.
 *
 * @returns {Promise<string>} The freshest access token available, "" if none.
 */
export async function ensureFreshToken(): Promise<string> {
    const keycloak = getKeycloak()

    if (!keycloak?.authenticated) {
        return getAccessToken()
    }

    try {
        await keycloak.updateToken(MIN_VALIDITY_SECONDS)
    } catch {
        console.warn("Access token renewal failed, falling back to stored token")
    }

    return keycloak.token || getAccessToken()
}

/**
 * Renews the access token regardless of how long it still claims to be valid.
 *
 * Used after a 401: the token we hold was refused, so its stated expiry cannot
 * be trusted. `updateToken(-1)` forces the refresh grant.
 *
 * @returns {Promise<string>} The new token, or "" when renewal failed.
 */
export async function forceRefreshToken(): Promise<string> {
    const keycloak = getKeycloak()

    if (!keycloak) {
        return ""
    }

    try {
        await keycloak.updateToken(-1)

        return keycloak.token || ""
    } catch {
        return ""
    }
}

/**
 * The stored access token, without attempting a renewal.
 *
 * @returns {string} The token, or "" when absent.
 */
export function getAccessToken(): string {
    const stored = localStorage.getItem(localStorageItemName.accessToken)

    // `onTokens` used to write the string "undefined" here whenever it fired
    // without tokens. Treat any such leftover as absent so a stale install never
    // sends `Bearer undefined`.
    if (!stored || stored === "undefined" || stored === "null") {
        return ""
    }

    return stored
}

/**
 * The role token of the currently selected tenant or domain.
 *
 * @returns {string} The token, or "" when no role is selected.
 */
export function getRoleToken(): string {
    try {
        return JSON.parse(localStorage.getItem(localStorageItemName.selectedRole) || "{}").token || ""
    } catch {
        return ""
    }
}

/**
 * Whether a network error carries an HTTP 401.
 *
 * Apollo surfaces the status on `networkError.statusCode`; `fetch` callers read
 * `response.status` themselves.
 *
 * @param {unknown} error - An Apollo `networkError`, or anything else.
 * @returns {boolean} True when the call was refused for lack of a valid token.
 */
export function isUnauthorized(error: unknown): boolean {
    return (error as {statusCode?: number} | null | undefined)?.statusCode === 401
}

/**
 * Whether the current URL is a Keycloak login callback.
 *
 * keycloak-js reads the authorization code and the `state` from the URL, and
 * matches that `state` against the PKCE code verifier it stashed in
 * `localStorage` under `kc-callback-<state>`. Anything that navigates away or
 * clears storage before `init()` has consumed the callback silently breaks the
 * login — so the app checks this before doing either.
 *
 * @returns {boolean} True while a login callback is pending.
 */
export function isAuthCallbackUrl(): boolean {
    const {hash, search} = window.location
    const params = new URLSearchParams(`${search.replace(/^\?/, "")}&${hash.replace(/^#/, "")}`)

    return params.has("state") && (params.has("code") || params.has("error") || params.has("access_token"))
}

/**
 * Clears the app's `localStorage` while preserving the given keys and every
 * keycloak-js bookkeeping entry.
 *
 * The `kc-callback-*` entries hold the PKCE code verifier and the login nonce.
 * A blanket `localStorage.clear()` used to take them with it, which made the
 * authorization-code exchange fail and sent the user straight back to Keycloak.
 *
 * @param {string[]} preservedKeys - App keys whose values must survive.
 */
export function clearAppStorage(preservedKeys: string[]): void {
    const preserved = new Map<string, string>()

    for (const key of preservedKeys) {
        const value = localStorage.getItem(key)
        if (value !== null) {
            preserved.set(key, value)
        }
    }

    for (let index = 0; index < localStorage.length; index++) {
        const key = localStorage.key(index)
        if (key?.startsWith("kc-callback-")) {
            preserved.set(key, localStorage.getItem(key) as string)
        }
    }

    localStorage.clear()

    for (const [key, value] of preserved) {
        localStorage.setItem(key, value)
    }
}
